/**
 * Menu « Créer » global (bouton « + » de la top bar) — présent sur TOUT écran.
 * Répond au « je ne trouve pas comment créer un nouveau projet » : une amorce unique,
 * toujours visible, qui pointe vers les points d'entrée du cycle (projet, idée, story,
 * programme). La création de projet ouvre le dialogue contrôlé monté dans la coquille
 * (via `onNewProject`) ; les autres actions naviguent vers l'écran d'amorce concerné.
 *
 * REFUS AVANT LE CLIC (audit du 2026-10-09, AUD-CRAWL-P14) : le serveur refuse toute écriture
 * sans rôle de realm propriétaire ou contributeur (`SecurityConfig.ROLES_ECRITURE`). Le lecteur
 * et l'administrateur de plateforme voyaient pourtant quatre créations actives, dont le refus
 * n'était dit qu'une fois le dialogue (ou l'écran) ouvert. Les entrées disent désormais leur
 * motif (`motifEcritureRefusee`) AVANT le clic : `aria-disabled`, décrites par le motif affiché
 * dans le menu, et inactives — mais toujours focalisables (APG « menu » : un élément désactivé
 * reste atteignable au clavier, sinon son motif n'est jamais lu).
 *
 * DEUX RÔLES, DEUX QUESTIONS (revue du 2026-10-09 ; D-14, audit du 2026-10-09) :
 *  - « Nouveau projet » et « Nouveau programme » suivent le rôle du JETON : la passerelle crée un
 *    projet ou un programme (`ProgramController.createProgram`) sur le seul rôle de realm, sans
 *    regarder le projet ouvert ; la palette ⌘K et l'écran des programmes jugent de même ;
 *  - « Nouvelle idée » et « Nouvelle story » s'écrivent DANS le projet ouvert : comme leurs écrans
 *    cibles (IdeationStudio, BacklogBoard) et la palette, elles suivent le rôle EFFECTIF
 *    (`useEffectiveSession` : l'appartenance au projet, plafonnée par le jeton). Un compte dont le
 *    jeton écrit mais qui n'est que LECTEUR du projet ouvert garde « Nouveau projet » et « Nouveau
 *    programme » et voit les deux autres refusées AVANT le clic.
 *
 * LE BANDEAU DIT LA CAUSE (N-41, revue du 2026-10-09) : « Administrateur de plateforme : … n'écrit pas
 * dans les projets » décrit un JETON sans rôle d'écriture. Un jeton `admin` + `workspace_owner` qui n'est
 * que lecteur du projet ouvert écrit : il lit « Lecture seule », au-dessus d'un « Nouveau projet » actif
 * (`useMotifEcritureRefusee`, qui juge la cause sur le jeton).
 */
import { useId, type JSX } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Boxes, Lightbulb, Columns3, Layers } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useEffectiveSession } from '@/hooks/useEffectiveSession'
import { useMotifEcritureRefusee } from '@/hooks/useMotifEcritureRefusee'
import { useSession } from '@/hooks/useSession'
import { useT } from '@/i18n/language'

/** `declencheur="tiroir"` : ligne « Créer… » du pied du tiroir téléphone (CoquilleTelephone). */
export function CreateMenu({ onNewProject, declencheur = 'barre' }: {
  onNewProject: () => void
  declencheur?: 'barre' | 'tiroir'
}): JSX.Element {
  const { t } = useT()
  const navigate = useNavigate()
  const jeton = useSession()
  const effective = useEffectiveSession()
  const idMotif = useId()
  // Le motif de `auth/session` est rédigé en français : le crochet le traduit par ses clés `ecriture.refus.*`
  // (CreateMenu.lecteur.test.tsx vérifie que le catalogue français le reprend mot pour mot).
  const motifProjet = useMotifEcritureRefusee(jeton) // « Nouveau projet » et « Nouveau programme » : le jeton seul
  // idée, story : le rôle dans le projet ouvert. Le libellé « administrateur » décrit le JETON
  // (N-41) : un jeton qui écrit mais lecteur du projet, même porteur de `admin`, lit « Lecture seule ».
  const motifContenu = useMotifEcritureRefusee(effective, jeton)
  // Un seul bandeau pour toutes les entrées refusées. Le rôle effectif est plafonné par le jeton : un
  // jeton qui refuse l'écriture la refuse aussi dans le projet, donc `motifContenu` couvre `motifProjet`.
  const motif = motifContenu ?? motifProjet
  /** Propriétés d'une entrée : active, ou refusée avec le motif de son rôle (le menu reste ouvert). */
  const entree = (refus: string | null, agir: () => void) => (refus === null
    ? { onSelect: agir }
    : {
        'aria-disabled': true, 'aria-describedby': idMotif, 'data-refuse': 'true', className: 'cursor-not-allowed opacity-60',
        onSelect: (e: Event) => e.preventDefault(),
      })

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {declencheur === 'tiroir' ? (
          <button type="button" data-testid="tiroir-creer"
                  className="flex min-h-10 w-full min-w-0 items-center gap-2 rounded-md px-2 text-left text-sm outline-none hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-sidebar-ring">
            <Plus className="size-4 shrink-0" aria-hidden="true" />
            <span className="min-w-0 flex-1">{t('create.menuTiroir', 'Créer…')}</span>
            <span className="text-xs text-muted-foreground">{t('create.menuTiroirDetail', 'projet, idée, story')}</span>
          </button>
        ) : (
        <Button variant="outline" size="sm" data-testid="create-menu" aria-label={t('create.menu', 'Créer')}
                className="shrink-0 gap-2">
          <Plus className="size-4" aria-hidden="true" />
          <span className="hidden sm:inline">{t('create.menu', 'Créer')}</span>
        </Button>
        )}
      </DropdownMenuTrigger>
      {/* Le motif tient sur plusieurs lignes : le menu s'élargit quand il l'affiche. */}
      <DropdownMenuContent align="end" className={motif === null ? 'w-52' : 'w-72'}>
        <DropdownMenuLabel>{t('create.menu', 'Créer')}</DropdownMenuLabel>
        {motif !== null ? (
          <DropdownMenuLabel id={idMotif} data-testid="create-menu-refus"
                             className="text-xs font-normal text-muted-foreground">
            {motif}
          </DropdownMenuLabel>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="create-project" {...entree(motifProjet, onNewProject)}>
          <Boxes className="size-4" aria-hidden="true" />
          {t('create.project', 'Nouveau projet')}
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="create-idea" {...entree(motifContenu, () => navigate('/ideas'))}>
          <Lightbulb className="size-4" aria-hidden="true" />
          {t('create.idea', 'Nouvelle idée')}
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="create-story" {...entree(motifContenu, () => navigate('/backlog'))}>
          <Columns3 className="size-4" aria-hidden="true" />
          {t('create.story', 'Nouvelle story')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="create-program" {...entree(motifProjet, () => navigate('/programs'))}>
          <Layers className="size-4" aria-hidden="true" />
          {t('create.program', 'Nouveau programme')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
